'use client'

import { useState } from 'react'
import Calendar from 'react-calendar'
import classNames from 'classnames'
import { useParams } from 'next/navigation'

import { useTranslation } from 'app/i18n/client'
import { Modal, Loading } from 'app/components/atoms'
import GuidelinePopup from 'app/components/atoms/DateRangePicker/GuidelinePopup'
import useWindowDimensions from 'app/hooks/useWindowDimensions'
import dayjs from 'lib/dayjs'
import { getJapanTimeZoneToday } from 'utils/toJapanTimeZone'
import ArrowLeft from 'public/icons/arrow-left.svg'
import ArrowRight from 'public/icons/arrow-right.svg'
import CloseIcon from 'public/icons/close.svg'

import { formatLowestPrice, getRoomPlanCalendarMonths } from 'tokyu-hooks'

import useQueryRoomPlanAvailability from 'app/hooks/useQueryRoomPlanAvailability'

interface RoomPlanCalendarModalProps {
  open: boolean
  hotelId?: string
  roomPlanCode: string
  nights: number
  adults: number
  // Not named `children` — passing a prop literally named `children` as a
  // JSX attribute (`children={...}`) trips react/no-children-prop.
  numberOfChildren: number
  // Number of rooms selected by the user. The availability query itself
  // stays 1-room semantics (matches the example Tripla widget), but the
  // displayed tile rate is multiplied by this so it lines up with the
  // room total the user clicked from.
  roomCount?: number
  onClose: () => void
  onSelectDate: (checkIn: Date, checkOut: Date) => void
}

const RoomPlanCalendarModal: React.FC<RoomPlanCalendarModalProps> = ({
  open,
  hotelId,
  roomPlanCode,
  nights,
  adults,
  numberOfChildren,
  roomCount = 1,
  onClose,
  onSelectDate,
}) => {
  const { t } = useTranslation()
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  // Same breakpoint convention as SearchResultGroup / the dated search
  // calendar (DateRangePicker): >=1024px is the desktop double-month popup,
  // below that is the mobile bottom sheet single-month view.
  const { width } = useWindowDimensions()
  const isDesktop = width >= 1024

  const today = getJapanTimeZoneToday()
  const currentMonth = today.startOf('month')

  const [displayedMonth, setDisplayedMonth] = useState(currentMonth)

  // One query per month: the visible month(s), the spillover months either
  // side of them (react-calendar renders leading days from the previous month
  // and trailing days from the next), and one month ahead as prefetch.
  // Month-aligned cache keys mean paging forward reuses the overlapping months
  // instantly and only fetches the new tail.
  const months = getRoomPlanCalendarMonths(
    displayedMonth.format('YYYY-MM'),
    currentMonth.format('YYYY-MM'),
  )

  const { data, isLoading, isError } = useQueryRoomPlanAvailability({
    hotelId,
    roomPlanCode,
    nights,
    adults,
    children: numberOfChildren,
    months,
  })

  const roomRates = data?.room_rates

  // Only block the UI while the months actually on screen have no data yet —
  // the neighbouring/prefetch months loading in the background shouldn't show
  // a spinner. Derived from displayedMonth rather than sliced off `months`,
  // whose leading entry is the previous month only on some pages.
  const visibleMonths = [0, ...(isDesktop ? [1] : [])].map((offset) =>
    displayedMonth.add(offset, 'month').format('YYYY-MM'),
  )
  const hasVisibleData =
    !!roomRates &&
    visibleMonths.some((month) =>
      Object.keys(roomRates).some((dateKey) =>
        dateKey.startsWith(month.replace('-', '/')),
      ),
    )
  const showLoading = isLoading && !hasVisibleData

  // Mirrors DateRangeSelect's own availability checks: past dates, or dates
  // missing from the response / without inventory, are not selectable.
  const isTileAvailable = (date: Date) => {
    const day = dayjs(date)
    if (day.isBefore(today, 'day')) return false

    const rate = roomRates?.[day.format('YYYY/MM/DD')]
    return rate?.availability === 'available' && (rate?.inventory ?? 0) > 0
  }

  // Bound paging the same way the previous implementation did: never before
  // the current JST month, never past the month the API's reservable period
  // ends in (when provided). Passed as react-calendar's own minDate/maxDate
  // so its built-in prev/next buttons (same ones the dated calendar uses)
  // auto-disable at the edges instead of custom button logic.
  const reservablePeriodEndDate = data?.reservable_period_end_date
    ? dayjs(data.reservable_period_end_date)
    : null
  const minNavDate = currentMonth.toDate()
  const maxNavDate = reservablePeriodEndDate
    ? reservablePeriodEndDate.endOf('month').toDate()
    : undefined

  return (
    <Modal
      position={isDesktop ? 'middle' : 'bottom'}
      open={open}
      onClose={onClose}
      containerClassName={classNames(
        '!pt-0 !px-0 !pb-0',
        !isDesktop && '!w-full !h-full',
      )}
    >
      {/* The mobile sheet's height hugs its content (TSE-352). The dated
          picker's sheet uses a fixed h-[730px], but this modal has no header
          bar or footer buttons, so a fixed height left a large empty gap
          below the calendar; max-h keeps it scrollable on six-row months. */}
      <div
        className={classNames(
          'relative bg-white shadow-lg text-left flex flex-col gap-2 pt-14 px-6 pb-6',
          isDesktop
            ? 'w-[1010px] max-w-[92vw] max-h-[90vh] overflow-y-auto'
            : '!w-full max-h-[100dvh] rounded-t-2.5xl overflow-y-auto',
        )}
      >
        <button
          type="button"
          aria-label={t('Close this modal')}
          className="absolute top-4 right-6 z-10"
          onClick={onClose}
        >
          <CloseIcon className="text-gray-light w-8 h-8" />
        </button>
        {/* Blue header bar — same markup/classes as the dated search
            calendar's PopupPicker header, but showing only the plan's night
            count since there's no check-in/check-out date yet. Desktop only
            (TSE-352): PM dropped it from the mobile web sheet. Hidden via
            Tailwind rather than `isDesktop &&` so SSR (useWindowDimensions
            starts at width 0) doesn't flash it hidden on desktop; `lg` is
            the default 1024px, matching `isDesktop`. */}
        <div className="hidden lg:block text-xs font-light uppercase px-4 py-2.5 bg-primary text-white">
          {t('{{n}} night(s)', { n: nights })}
        </div>
        <div className="relative">
          {showLoading && (
            <div className="absolute top-0 left-0 w-full h-full bg-[#F6F6F6] bg-opacity-80 z-[500] flex items-center justify-center">
              <Loading
                className="!text-[#F6F6F6] text-opacity-80"
                baseClassName="!text-primary"
              />
            </div>
          )}
          {isError && (
            <div className="mt-8 w-full text-center text-error">
              {t('There are something wrong')}
            </div>
          )}
          {!isError && (
            // Reuse the search calendar's engine: the same `react-calendar`
            // component the `@wojtekmaj/react-daterange-picker` wrapper renders
            // internally, wrapped in `.room-plan-calendar` so the undated
            // layout (double-month, nav bar, weekday header, tile sizing, price
            // tiles) comes from that scoped block in app/styles/calendar.css.
            // The dated search calendar keeps the production styling, so these
            // rules must never be scoped to `.react-daterange-picker`.
            // We render the bare Calendar instead of the range-picker wrapper
            // because DateRangeSelect's onChange only fires once two clicks
            // complete a range — this modal needs a single click to pick one
            // date, so paging/selection are driven directly against
            // react-calendar's own props (same ones DateRangeSelect forwards
            // via calendarProps) rather than through the range wrapper.
            <div className="room-plan-calendar w-full">
              <Calendar
                className="w-full"
                calendarType="gregory"
                locale={locale}
                activeStartDate={displayedMonth.toDate()}
                minDetail="month"
                maxDetail="month"
                showDoubleView={isDesktop}
                minDate={minNavDate}
                maxDate={maxNavDate}
                prevLabel={<ArrowLeft className="fill-current text-gray" />}
                nextLabel={<ArrowRight className="fill-current text-gray" />}
                formatDay={(_: string | undefined, date: Date) =>
                  dayjs(date).get('date')?.toString()
                }
                onActiveStartDateChange={({
                  activeStartDate,
                }: {
                  activeStartDate: Date | null
                }) => {
                  if (!activeStartDate) return
                  setDisplayedMonth(dayjs(activeStartDate))
                }}
                tileDisabled={({ date }: { date: Date }) =>
                  !isTileAvailable(date)
                }
                tileClassName={({ date }: { date: Date }) => {
                  if (isTileAvailable(date)) return null
                  // Past dates keep the default disabled gray; future
                  // sold-out/missing dates get the same #A7A7A7 tone
                  // DateRangeSelect uses for future sold-out dates.
                  return dayjs(date).isSameOrAfter(today, 'day')
                    ? 'sold-out-future'
                    : null
                }}
                tileContent={({ date, view }: { date: Date; view: string }) => {
                  if (view !== 'month') return null

                  if (!isTileAvailable(date)) {
                    // Past dates stay empty like the dated calendar;
                    // only today-or-later sold-out dates show the
                    // waitlist text.
                    if (dayjs(date).isBefore(today, 'day')) return null
                    return (
                      <span className="calendar-tile__sub calendar-tile__waitlist">
                        {t('Waitlist')}
                      </span>
                    )
                  }

                  const rate = roomRates?.[dayjs(date).format('YYYY/MM/DD')]
                  const { line1, line2 } = formatLowestPrice(
                    (rate?.rate ?? 0) * roomCount,
                    locale,
                  )
                  // ja shows the amount on the first line ("16,000" /
                  // "円〜"); other locales show it on the second ("JPY" /
                  // "16,000〜") — same split as DateRangeSelect's price
                  // tile.
                  const amountFirst = locale === 'ja'
                  const unitClassName = amountFirst
                    ? 'calendar-tile__price-unit'
                    : 'calendar-tile__price-unit calendar-tile__price-unit--jpy'

                  return (
                    <span className="calendar-tile__sub calendar-tile__price">
                      <span
                        className={
                          amountFirst
                            ? 'calendar-tile__price-amount'
                            : unitClassName
                        }
                      >
                        {line1}
                      </span>
                      <span
                        className={
                          amountFirst
                            ? unitClassName
                            : 'calendar-tile__price-amount'
                        }
                      >
                        {line2}
                      </span>
                    </span>
                  )
                }}
                onClickDay={(value: Date) => {
                  if (!isTileAvailable(value)) return
                  const clickedDate = dayjs(value)
                  const checkIn = clickedDate.toDate()
                  const checkOut = clickedDate.add(nights, 'day').toDate()
                  onSelectDate(checkIn, checkOut)
                }}
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-center">
          <GuidelinePopup />
        </div>
      </div>
    </Modal>
  )
}

export default RoomPlanCalendarModal
